import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import { registerAllModules } from 'handsontable/registry';
import { debounce } from 'lodash';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
} from '@mui/material';
import 'handsontable/dist/handsontable.full.min.css';
import '../utils/containerDispatchPlaceholderRenderer';
import FullScreenButton from './FullScreenButton';
import { auth, db } from '../firebaseConfig';
import { socket } from '../socket';
import { collection, getDocs } from 'firebase/firestore';
import { notifyContainerDispatchDateSaved } from '../utils/containerDispatchEvents';
import { formatPhoneKoreanInput } from '../utils/phone';
import { formatAmountDisplay, formatAmountKoreanInput } from '../utils/numberUtils';
import { computeRouteMemoHoverPosition } from '../utils/routeMemoUi';
import { PLACEHOLDER_RENDERER, AMOUNT_RENDERER } from '../utils/containerDispatchPlaceholderRenderer';
import { CONTAINER_CARRIER_COLLECTION } from './CompanyInfoGrid';
import {
  EMPTY_CONTAINER_MASTER_GROUPED,
  type ContainerMasterGrouped,
} from '../utils/containerDispatchMaster';
import ContainerDispatchGridHeader from './ContainerDispatchGridHeader';
import GridSuggestOverlay, {
  type GridSuggestOverlayHandle,
  type GridSuggestState,
} from './GridSuggestOverlay';
import {
  computeSuggestDropdownPosition,
  filterSuggestOptions,
  findCanonicalSuggestValue,
  getStrictSuggestRejectMessage,
  isStrictSuggestValue,
} from '../utils/gridSuggestDropdown';
import './ContainerDispatchReportPage.css';
import {
  GRID_COL_COUNT,
  ROWS_PER_RECORD,
  CUSTOM_HEADER_HEIGHT,
  CD_ROW_HEADER_WIDTH,
  CD_DEFAULT_COL_WIDTHS,
  scaleColumnWidthsToFit,
  tablePixelWidth,
  apiRowsToLogicalLayout,
  buildAllMergeCells,
  buildRowPayload,
  calcLogicalRecordsForHeight,
  emptyLogicalRecord,
  gridToLogicalRecords,
  isLogicalRecordEmpty,
  logicalIndexFromVisualRow,
  logicalRecordsToGrid,
  MIN_LOGICAL_RECORDS,
  padLogicalRecords,
  type ContainerRowApi,
} from '../utils/containerDispatchGrid';

registerAllModules();

const ROW_HEIGHT = 32;
const SIDEBAR_RESERVE_EXPANDED = 242;
const SIDEBAR_RESERVE_COLLAPSED = 58;

interface Props {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
  selectedDate?: string;
  setSelectedDate: (date: string | undefined) => void;
  sidebarCollapsed?: boolean;
}

interface RowMeta {
  rowId: string | null;
  version: number;
  shipperMemo: string;
}

const emptyRowMeta = (): RowMeta => ({ rowId: null, version: 1, shipperMemo: '' });

function currentUserPayload() {
  return {
    userId: auth.currentUser?.uid,
    userEmail: auth.currentUser?.email ?? undefined,
    changedBy: auth.currentUser?.email || auth.currentUser?.uid || 'anonymous',
  };
}

type SaveStatus = 'idle' | 'error';

type CellSuggestState = GridSuggestState;

const ContainerDispatchReportPage: React.FC<Props> = ({
  isFullScreen,
  setIsFullScreen,
  selectedDate,
  setSelectedDate,
  sidebarCollapsed = false,
}) => {
  const hotRef = useRef<any>(null);
  const rowMetaRef = useRef<RowMeta[]>([]);
  const loadSeqRef = useRef(0);
  const loadingRef = useRef(false);
  const dispatchDateRef = useRef('');
  const debouncersRef = useRef<Map<number, ReturnType<typeof debounce>>>(new Map());
  const lastPayloadRef = useRef<Map<number, string>>(new Map());
  const persistRowRef = useRef<(logicalIndex: number) => Promise<void>>(async () => {});
  const persistInFlightRef = useRef<Map<number, Promise<void>>>(new Map());
  const gridWrapRef = useRef<HTMLDivElement>(null);
  const tableDataRef = useRef<string[][]>(logicalRecordsToGrid(padLogicalRecords([])));
  const statusLabelRef = useRef<HTMLSpanElement>(null);
  const amountEditorCleanupRef = useRef<(() => void) | null>(null);
  const suggestEditorCleanupRef = useRef<(() => void) | null>(null);
  const suggestOriginalRef = useRef<{ row: number; col: number; value: string } | null>(null);
  const suggestEditingCellRef = useRef<{ row: number; col: number } | null>(null);
  const suggestSelectLockRef = useRef(false);
  const cellSuggestRef = useRef<CellSuggestState | null>(null);
  const suggestOverlayRef = useRef<GridSuggestOverlayHandle>(null);
  const companyNamesRef = useRef<string[]>([]);
  const carrierNamesRef = useRef<string[]>([]);
  const masterRef = useRef<ContainerMasterGrouped>(EMPTY_CONTAINER_MASTER_GROUPED);
  const minLogicalRecordsRef = useRef(MIN_LOGICAL_RECORDS);
  const [dispatchDate, setDispatchDate] = useState(() => selectedDate || '');
  const [tableData, setTableData] = useState<string[][]>(() =>
    logicalRecordsToGrid(padLogicalRecords([]))
  );
  const [gridHeight, setGridHeight] = useState(480);
  const [colWidths, setColWidths] = useState<number[]>(() => [...CD_DEFAULT_COL_WIDTHS]);
  const hotHeight = Math.max(160, gridHeight - CUSTOM_HEADER_HEIGHT);
  const tableWidth = useMemo(
    () => tablePixelWidth(colWidths, CD_ROW_HEADER_WIDTH),
    [colWidths]
  );
  const [minLogicalRecords, setMinLogicalRecords] = useState(MIN_LOGICAL_RECORDS);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [lastEditInfo, setLastEditInfo] = useState<{
    email: string;
    name: string;
    timestamp: string;
  } | null>(null);
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [carrierNames, setCarrierNames] = useState<string[]>([]);
  const [masterData, setMasterData] = useState<ContainerMasterGrouped>(EMPTY_CONTAINER_MASTER_GROUPED);
  const [shipperMemoHover, setShipperMemoHover] = useState<{
    visualRow: number;
    left: number;
    top: number;
    text: string;
  } | null>(null);
  const [shipperMemoEdit, setShipperMemoEdit] = useState<{
    logicalIndex: number;
    rowId: string;
    text: string;
  } | null>(null);
  const [shipperMemoSaving, setShipperMemoSaving] = useState(false);
  const [shipperMemoCopySnackbar, setShipperMemoCopySnackbar] = useState(false);
  const [suggestRejectSnackbar, setSuggestRejectSnackbar] = useState<string | null>(null);
  const shipperMemoHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shipperMemoSuppressHoverRef = useRef(false);
  const shipperMemoInputRef = useRef<HTMLTextAreaElement | null>(null);
  const openShipperMemoEditorRef = useRef<(logicalIndex: number) => void>(() => {});
  const saveShipperMemoRef = useRef<(logicalIndex: number, memo: string) => Promise<boolean>>(async () => false);
  const isShipperMemoEligibleRef = useRef<(logicalIndex: number) => boolean>(() => false);
  const getShipperMemoRef = useRef<(logicalIndex: number) => string>(() => '');

  const getSuggestSource = useCallback((row: number, col: number): {
    title: string;
    options: string[];
    /** false면 목록 외 임의 입력 허용 (자동완성은 유지) */
    strict?: boolean;
  } | null => {
    const isTopRow = row % ROWS_PER_RECORD === 0;
    if (col === 0 && isTopRow && companyNamesRef.current.length > 0) {
      return { title: '거래처', options: companyNamesRef.current };
    }
    if (col === 10 && isTopRow && carrierNamesRef.current.length > 0) {
      return { title: '운송업체', options: carrierNamesRef.current };
    }
    if (col === 1 && isTopRow && masterRef.current.shipper.length > 0) {
      return { title: '도착 창고 및 업체', options: masterRef.current.shipper };
    }
    if (col === 1 && !isTopRow && masterRef.current.region.length > 0) {
      return { title: '도착지역', options: masterRef.current.region, strict: false };
    }
    if (col === 4 && isTopRow && masterRef.current.line.length > 0) {
      return { title: 'LINE', options: masterRef.current.line };
    }
    if (col === 9 && masterRef.current.terminal.length > 0) {
      return { title: '터미널', options: masterRef.current.terminal };
    }
    return null;
  }, []);

  const notifyStrictSuggestReject = useCallback((row: number, col: number) => {
    const message = getStrictSuggestRejectMessage(row, col, ROWS_PER_RECORD);
    if (message) setSuggestRejectSnackbar(message);
  }, []);

  const closeCellSuggest = useCallback(() => {
    suggestEditorCleanupRef.current?.();
    suggestEditorCleanupRef.current = null;
    suggestOverlayRef.current?.hide();
    cellSuggestRef.current = null;
    suggestEditingCellRef.current = null;
  }, []);

  const updateCellSuggest = useCallback((next: CellSuggestState | null) => {
    if (suggestSelectLockRef.current && next) return;
    cellSuggestRef.current = next;
    if (next) {
      suggestOverlayRef.current?.update(next);
    } else {
      suggestOverlayRef.current?.hide();
    }
  }, []);

  const isHandsontableEditing = useCallback((hot: Handsontable) => {
    const input = hot.rootElement?.querySelector('textarea.handsontableInput');
    return Boolean(input && document.activeElement === input);
  }, []);

  const validateSuggestCell = useCallback((row: number, col: number) => {
    const hot = hotRef.current?.hotInstance as Handsontable | undefined;
    if (!hot) return;
    const source = getSuggestSource(row, col);
    if (!source) {
      suggestOriginalRef.current = null;
      suggestEditingCellRef.current = null;
      return;
    }
    const raw = String(hot.getDataAtCell(row, col) ?? '');
    const val = raw.trim();
    if (!val) {
      if (raw !== '') {
        hot.setDataAtCell(row, col, '', 'cd-strict-reject');
      }
      suggestOriginalRef.current = null;
      suggestEditingCellRef.current = null;
      return;
    }
    const canonical = findCanonicalSuggestValue(val, source.options);
    if (!canonical) {
      if (source.strict === false) {
        if (val !== raw) {
          hot.setDataAtCell(row, col, val, 'cd-strict-normalize');
        }
      } else {
        const original = suggestOriginalRef.current;
        const revert = original && original.row === row && original.col === col
          ? original.value
          : '';
        hot.setDataAtCell(row, col, revert, 'cd-strict-reject');
        notifyStrictSuggestReject(row, col);
      }
    } else if (canonical !== raw) {
      // 소문자 입력 등 → 목록의 정식 표기로 정규화 (예: skr → SKR)
      hot.setDataAtCell(row, col, canonical, 'cd-strict-normalize');
    }
    suggestOriginalRef.current = null;
    suggestEditingCellRef.current = null;
  }, [getSuggestSource, notifyStrictSuggestReject]);

  const scheduleSuggestValidation = useCallback((row: number, col: number) => {
    const run = () => {
      const hot = hotRef.current?.hotInstance as Handsontable | undefined;
      if (!hot) return;
      // 편집 중이면 조금 뒤에 다시 시도 (확정 직후 textarea 잔여 포커스 대비)
      if (isHandsontableEditing(hot)) {
        window.setTimeout(run, 30);
        return;
      }
      validateSuggestCell(row, col);
      closeCellSuggest();
    };
    window.setTimeout(run, 0);
  }, [closeCellSuggest, isHandsontableEditing, validateSuggestCell]);

  useEffect(() => {
    let cancelled = false;
    const loadCompanyNames = async () => {
      try {
        const snap = await getDocs(collection(db, 'dispatch_company'));
        const names: string[] = [];
        snap.forEach(docSnap => {
          const company = docSnap.data().company;
          if (typeof company === 'string') {
            const trimmed = company.trim();
            if (trimmed) names.push(trimmed);
          }
        });
        names.sort((a, b) => a.localeCompare(b, 'ko'));
        if (cancelled) return;
        companyNamesRef.current = names;
        setCompanyNames(names);
      } catch (e) {
        console.error('[ContainerDispatch] 거래처 목록 로드 실패:', e);
      }
    };
    void loadCompanyNames();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCarrierNames = async () => {
      try {
        const snap = await getDocs(collection(db, CONTAINER_CARRIER_COLLECTION));
        const names: string[] = [];
        snap.forEach(docSnap => {
          const company = docSnap.data().company;
          if (typeof company === 'string') {
            const trimmed = company.trim();
            if (trimmed) names.push(trimmed);
          }
        });
        names.sort((a, b) => a.localeCompare(b, 'ko'));
        if (cancelled) return;
        carrierNamesRef.current = names;
        setCarrierNames(names);
      } catch (e) {
        console.error('[ContainerDispatch] 운송업체 목록 로드 실패:', e);
      }
    };
    void loadCarrierNames();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadMaster = async () => {
      try {
        const res = await fetch('/api/container-dispatch/master');
        if (!res.ok) throw new Error('마스터 조회 실패');
        const data: ContainerMasterGrouped = await res.json();
        const grouped: ContainerMasterGrouped = {
          shipper: Array.isArray(data.shipper) ? data.shipper : [],
          region: Array.isArray(data.region) ? data.region : [],
          line: Array.isArray(data.line) ? data.line : [],
          terminal: Array.isArray(data.terminal) ? data.terminal : [],
        };
        if (cancelled) return;
        masterRef.current = grouped;
        setMasterData(grouped);
      } catch (e) {
        console.error('[ContainerDispatch] 마스터 목록 로드 실패:', e);
      }
    };
    void loadMaster();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    const hasMaster =
      masterData.shipper.length > 0
      || masterData.region.length > 0
      || masterData.line.length > 0
      || masterData.terminal.length > 0;
    if (hot && (companyNames.length > 0 || carrierNames.length > 0 || hasMaster)) hot.render();
  }, [companyNames, carrierNames, masterData]);

  const applyGridData = useCallback((grid: string[][]) => {
    tableDataRef.current = grid;
    setTableData(grid);
    const hot = hotRef.current?.hotInstance;
    if (hot) hot.loadData(grid);
  }, []);

  const readGridData = useCallback((): string[][] => {
    const hot = hotRef.current?.hotInstance;
    if (hot) {
      const data = hot.getData() as string[][];
      tableDataRef.current = data;
      return data;
    }
    return tableDataRef.current;
  }, []);

  dispatchDateRef.current = dispatchDate;
  loadingRef.current = loading;
  minLogicalRecordsRef.current = minLogicalRecords;

  const mergeCells = useMemo(
    () => buildAllMergeCells(tableData.length),
    [tableData.length]
  );

  const ensureMinRecords = useCallback((records: ReturnType<typeof gridToLogicalRecords>) => {
    return padLogicalRecords(records, minLogicalRecordsRef.current);
  }, []);

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;

    const updateSize = () => {
      const h = el.clientHeight;
      const w = el.clientWidth;
      if (h < 120) return;
      setGridHeight(h);
      const dataHeight = Math.max(0, h - CUSTOM_HEADER_HEIGHT);
      const visualRows = tableDataRef.current.length;
      const needsVScroll = visualRows * ROW_HEIGHT > dataHeight;
      const scrollReserve = needsVScroll ? 17 : 0;
      setColWidths(scaleColumnWidthsToFit(w - scrollReserve, CD_ROW_HEADER_WIDTH));
      const logical = calcLogicalRecordsForHeight(h, ROW_HEIGHT);
      setMinLogicalRecords(prev => (prev === logical ? prev : logical));
    };

    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    window.addEventListener('resize', updateSize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [isFullScreen, loading]);

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el || loading) return;
    const h = el.clientHeight;
    const w = el.clientWidth;
    if (h < 120) return;
    const dataHeight = Math.max(0, h - CUSTOM_HEADER_HEIGHT);
    const needsVScroll = tableData.length * ROW_HEIGHT > dataHeight;
    setColWidths(scaleColumnWidthsToFit(w - (needsVScroll ? 17 : 0), CD_ROW_HEADER_WIDTH));
  }, [tableData.length, loading]);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    const base = hot ? (hot.getData() as string[][]) : tableDataRef.current;
    const records = gridToLogicalRecords(base);
    const padded = ensureMinRecords(records);
    if (padded.length === records.length) return;
    const next = logicalRecordsToGrid(padded);
    applyGridData(next);
    while (rowMetaRef.current.length < padded.length) {
      rowMetaRef.current.push(emptyRowMeta());
    }
  }, [minLogicalRecords, ensureMinRecords, applyGridData]);

  useEffect(() => {
    if (!selectedDate) {
      loadSeqRef.current += 1;
      setDispatchDate('');
      setLoading(false);
      setSaveMessage('');
      rowMetaRef.current = [];
      lastPayloadRef.current.clear();
      debouncersRef.current.forEach(fn => fn.cancel());
      debouncersRef.current.clear();
      applyGridData(logicalRecordsToGrid(padLogicalRecords([])));
      return;
    }
    if (selectedDate !== dispatchDate) {
      setDispatchDate(selectedDate);
    }
  }, [selectedDate, applyGridData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    debouncersRef.current.forEach(fn => fn.cancel());
    debouncersRef.current.clear();
    lastPayloadRef.current.clear();
  }, [dispatchDate]);

  const loadRows = useCallback(async (date: string) => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setSaveMessage('');
    try {
      const res = await fetch(`/api/container-dispatch/${date}/rows`);
      if (!res.ok) throw new Error('컨테이너 배차일보를 불러오지 못했습니다.');
      const data = await res.json();
      if (seq !== loadSeqRef.current) return;
      const apiRows: ContainerRowApi[] = data.rows || [];
      const { records: padded, meta } = apiRowsToLogicalLayout(apiRows);
      rowMetaRef.current = meta;
      const grid = logicalRecordsToGrid(padded);
      applyGridData(grid);
      lastPayloadRef.current.clear();
      padded.forEach((rec, idx) => {
        if (!isLogicalRecordEmpty(rec)) {
          lastPayloadRef.current.set(idx, JSON.stringify(buildRowPayload(rec, idx, rowMetaRef.current[idx])));
        }
      });
    } catch (e: unknown) {
      if (seq !== loadSeqRef.current) return;
      const msg = e instanceof Error ? e.message : '조회 실패';
      if (msg === 'Failed to fetch') {
        alert('서버에 연결할 수 없습니다. 백엔드가 실행 중인지 확인하세요.');
      } else {
        alert(msg);
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [applyGridData]);

  useEffect(() => {
    if (!dispatchDate) return;
    setPendingChangesCount(0);
    setLastEditInfo(null);
    loadRows(dispatchDate);
  }, [dispatchDate, loadRows]);

  useEffect(() => {
    const handleRemoteEdit = (data: {
      date?: string;
      source?: string;
      userId?: string;
      userEmail?: string;
      userName?: string;
      timestamp?: string;
    }) => {
      if (data.source !== 'container-dispatch') return;
      const eventDate = data.date;
      const viewingDate = dispatchDateRef.current;
      if (!eventDate || !viewingDate || eventDate !== viewingDate) return;

      const uid = auth.currentUser?.uid;
      const myEmail = auth.currentUser?.email || '';
      if (data.userId && uid && data.userId === uid) return;
      if (data.userEmail && myEmail && data.userEmail === myEmail) return;

      const editEmail = data.userEmail || '';
      const editName = data.userName || editEmail.split('@')[0] || '다른 사용자';
      setLastEditInfo({
        email: editEmail,
        name: editName,
        timestamp: data.timestamp || new Date().toISOString(),
      });
      setPendingChangesCount(prev => prev + 1);
    };

    socket.on('report-updated', handleRemoteEdit);
    return () => {
      socket.off('report-updated', handleRemoteEdit);
    };
  }, []);

  const handleDateChange = (date: string) => {
    setDispatchDate(date);
    setSelectedDate(date);
  };

  const getLogicalRecordAt = (logicalIndex: number) => {
    const data = readGridData();
    const records = gridToLogicalRecords(data);
    return records[logicalIndex];
  };

  const applySavedRowMeta = (logicalIndex: number, beforeRowId: string | null, savedRows: ContainerRowApi[]) => {
    const matched = savedRows.find(r =>
      (beforeRowId && r.rowId === beforeRowId) || r.sortOrder === logicalIndex
    ) || savedRows[logicalIndex];
    if (matched?.rowId) {
      const prev = rowMetaRef.current[logicalIndex];
      rowMetaRef.current[logicalIndex] = {
        rowId: matched.rowId,
        version: matched.version || 1,
        shipperMemo: matched.shipperMemo ?? prev?.shipperMemo ?? '',
      };
    }
  };

  const releaseHandsontableFocus = useCallback(() => {
    const hot = hotRef.current?.hotInstance as Handsontable | undefined;
    if (!hot) return;
    const editor = hot.getActiveEditor?.();
    if (editor && typeof editor.finishEditing === 'function') {
      try {
        if (typeof editor.isOpened === 'function' && editor.isOpened()) {
          editor.finishEditing(false, false);
        }
      } catch {
        // 편집 중이 아니면 무시
      }
    }
    if (typeof (hot as any).deselectCell === 'function') {
      (hot as any).deselectCell();
    }
    const textarea = hot.rootElement?.querySelector('textarea.handsontableInput');
    if (textarea instanceof HTMLElement) {
      textarea.blur();
    }
  }, []);

  const focusShipperMemoInput = useCallback(() => {
    releaseHandsontableFocus();
    window.setTimeout(() => {
      const el = shipperMemoInputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }, 0);
  }, [releaseHandsontableFocus]);

  const copyShipperMemoText = useCallback(async (text: string) => {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return;
    try {
      await navigator.clipboard.writeText(trimmed);
      setShipperMemoCopySnackbar(true);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = trimmed;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setShipperMemoCopySnackbar(true);
      } catch {
        window.alert('클립보드 복사에 실패했습니다.');
      }
    }
  }, []);

  const clearShipperMemoHoverTimers = () => {
    if (shipperMemoHoverTimerRef.current) {
      clearTimeout(shipperMemoHoverTimerRef.current);
      shipperMemoHoverTimerRef.current = null;
    }
  };

  const dismissShipperMemoHover = useCallback(() => {
    clearShipperMemoHoverTimers();
    setShipperMemoHover(null);
  }, []);

  const suppressShipperMemoHover = useCallback(() => {
    shipperMemoSuppressHoverRef.current = true;
    dismissShipperMemoHover();
  }, [dismissShipperMemoHover]);

  const resumeShipperMemoHover = useCallback(() => {
    shipperMemoSuppressHoverRef.current = false;
  }, []);

  const isShipperMemoEligible = useCallback((logicalIndex: number) => {
    const meta = rowMetaRef.current[logicalIndex];
    const rec = getLogicalRecordAt(logicalIndex);
    if (!meta?.rowId || !rec) return false;
    return !!(rec.customer.trim() && rec.shipperName.trim());
  }, [readGridData]);

  const getShipperMemo = useCallback((logicalIndex: number) => {
    return String(rowMetaRef.current[logicalIndex]?.shipperMemo ?? '').trim();
  }, []);

  const cancelPendingRowSave = useCallback((logicalIndex: number) => {
    debouncersRef.current.get(logicalIndex)?.cancel();
  }, []);

  const awaitRowPersist = useCallback(async (logicalIndex: number) => {
    const pending = persistInFlightRef.current.get(logicalIndex);
    if (pending) await pending;
  }, []);

  const saveShipperMemo = useCallback(async (logicalIndex: number, shipperMemo: string) => {
    const date = dispatchDateRef.current;
    if (!date) return false;

    const meta = rowMetaRef.current[logicalIndex];
    const rec = getLogicalRecordAt(logicalIndex);
    if (!meta?.rowId || !rec) return false;

    const customer = rec.customer.trim();
    const shipperName = rec.shipperName.trim();
    if (shipperMemo.trim() !== '' && (!customer || !shipperName)) return false;

    cancelPendingRowSave(logicalIndex);
    await awaitRowPersist(logicalIndex);

    const prevMeta = meta;
    const nextMeta = { ...meta, shipperMemo };

    try {
      const res = await fetch(`/api/container-dispatch/${date}/rows/${meta.rowId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipperMemo,
          ...currentUserPayload(),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || '저장 실패');

      const savedRow: ContainerRowApi | undefined = body.row;
      if (savedRow?.rowId) {
        rowMetaRef.current[logicalIndex] = {
          rowId: savedRow.rowId,
          version: savedRow.version || prevMeta.version + 1,
          shipperMemo: savedRow.shipperMemo ?? shipperMemo,
        };
        lastPayloadRef.current.set(
          logicalIndex,
          JSON.stringify(buildRowPayload(rec, logicalIndex, rowMetaRef.current[logicalIndex]))
        );
      } else {
        rowMetaRef.current[logicalIndex] = nextMeta;
      }
      hotRef.current?.hotInstance?.render();
      return true;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '메모 저장 실패';
      alert(msg);
      return false;
    }
  }, [cancelPendingRowSave, awaitRowPersist, readGridData]);

  const openShipperMemoEditor = useCallback((logicalIndex: number) => {
    dismissShipperMemoHover();
    const meta = rowMetaRef.current[logicalIndex];
    const rec = getLogicalRecordAt(logicalIndex);
    if (!meta?.rowId || !rec) return;
    if (!rec.customer.trim() || !rec.shipperName.trim()) return;
    const text = rowMetaRef.current[logicalIndex]?.shipperMemo ?? '';
    releaseHandsontableFocus();
    setShipperMemoEdit({ logicalIndex, rowId: meta.rowId, text });
  }, [dismissShipperMemoHover, releaseHandsontableFocus, readGridData]);

  isShipperMemoEligibleRef.current = isShipperMemoEligible;
  getShipperMemoRef.current = getShipperMemo;
  saveShipperMemoRef.current = saveShipperMemo;
  openShipperMemoEditorRef.current = openShipperMemoEditor;

  useEffect(() => {
    if (!shipperMemoEdit) return;
    focusShipperMemoInput();
  }, [shipperMemoEdit, focusShipperMemoInput]);

  useEffect(() => () => clearShipperMemoHoverTimers(), []);

  const shipperTopRenderer = useCallback((
    instance: Handsontable,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: unknown,
    cellProperties: Handsontable.CellProperties
  ) => {
    const placeholderRenderer = (Handsontable.renderers as unknown as {
      getRenderer: (name: string) => typeof Handsontable.renderers.TextRenderer;
    }).getRenderer(PLACEHOLDER_RENDERER);
    placeholderRenderer(instance, td, row, col, prop, value, cellProperties);

    const logicalIndex = logicalIndexFromVisualRow(row);
    const shipperMemo = getShipperMemoRef.current(logicalIndex);
    td.style.position = 'relative';
    td.style.overflow = 'hidden';
    td.style.textOverflow = 'ellipsis';
    td.style.whiteSpace = 'nowrap';

    const existingIndicator = td.querySelector('.route-memo-indicator');
    if (existingIndicator) existingIndicator.remove();

    if (shipperMemo) {
      td.classList.add('has-route-memo');
      const indicator = document.createElement('span');
      indicator.className = 'route-memo-indicator';
      indicator.setAttribute('aria-label', '메모 있음');
      td.appendChild(indicator);

      td.onmouseenter = () => {
        if (shipperMemoSuppressHoverRef.current) return;
        clearShipperMemoHoverTimers();
        shipperMemoHoverTimerRef.current = setTimeout(() => {
          if (shipperMemoSuppressHoverRef.current) return;
          const rect = td.getBoundingClientRect();
          const pos = computeRouteMemoHoverPosition(rect, shipperMemo);
          setShipperMemoHover({
            visualRow: row,
            left: pos.left,
            top: pos.top,
            text: shipperMemo,
          });
        }, 200);
      };
      td.onmouseleave = () => {
        if (shipperMemoHoverTimerRef.current) {
          clearTimeout(shipperMemoHoverTimerRef.current);
          shipperMemoHoverTimerRef.current = null;
        }
      };
      td.onmousedown = (e: MouseEvent) => {
        if (e.button === 2) suppressShipperMemoHover();
      };
      td.oncontextmenu = () => {
        suppressShipperMemoHover();
      };
    } else {
      td.classList.remove('has-route-memo');
      td.onmouseenter = null;
      td.onmouseleave = null;
      td.onmousedown = null;
      td.oncontextmenu = null;
    }
    return td;
  }, [suppressShipperMemoHover]);

  const updateStatusLabel = (text: string, isError = false) => {
    const el = statusLabelRef.current;
    if (!el) return;
    el.textContent = text;
    el.style.color = isError ? '#c62828' : '#888';
    el.style.fontWeight = isError ? '600' : '400';
  };

  const persistDeletes = useCallback(async (deleteIds: string[]) => {
    if (loadingRef.current || deleteIds.length === 0) return;
    const date = dispatchDateRef.current;
    if (!date) return;

    try {
      const res = await fetch(`/api/container-dispatch/${date}/rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows: [],
          deleteIds: [...new Set(deleteIds)],
          ...currentUserPayload(),
        }),
      });
      const body = await res.json();
      if (res.status === 409) {
        setSaveStatus('error');
        setSaveMessage('다른 사용자가 수정했습니다. 새로고침합니다.');
        await loadRows(date);
        return;
      }
      if (!res.ok) throw new Error(body.error || '삭제 실패');
      const now = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
      updateStatusLabel(`마지막 저장: ${now}`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '삭제 실패';
      setSaveStatus('error');
      setSaveMessage(msg);
      updateStatusLabel(msg, true);
    }
  }, [loadRows]);

  const persistRow = useCallback(async (logicalIndex: number) => {
    if (loadingRef.current) return;
    const date = dispatchDateRef.current;
    if (!date) return;

    const rec = getLogicalRecordAt(logicalIndex);
    if (!rec) return;

    const meta = rowMetaRef.current[logicalIndex] || emptyRowMeta();
    const beforeRowId = meta.rowId;
    const deleteIds: string[] = [];
    const rows: Array<Record<string, unknown>> = [];

    if (!isLogicalRecordEmpty(rec)) {
      rows.push(buildRowPayload(rec, logicalIndex, meta));
    } else if (meta.rowId) {
      deleteIds.push(meta.rowId);
    }

    if (rows.length === 0 && deleteIds.length === 0) return;

    const payloadKey = rows.length > 0
      ? JSON.stringify(rows[0])
      : `__delete__${deleteIds.join(',')}`;
    if (lastPayloadRef.current.get(logicalIndex) === payloadKey) return;

    try {
      const res = await fetch(`/api/container-dispatch/${date}/rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows,
          deleteIds,
          ...currentUserPayload(),
        }),
      });
      const body = await res.json();
      if (res.status === 409) {
        setSaveStatus('error');
        setSaveMessage('다른 사용자가 수정했습니다. 새로고침합니다.');
        updateStatusLabel('다른 사용자가 수정했습니다. 새로고침합니다.', true);
        lastPayloadRef.current.delete(logicalIndex);
        await loadRows(date);
        return;
      }
      if (!res.ok) throw new Error(body.error || '저장 실패');

      const savedRows: ContainerRowApi[] = body.rows || [];
      if (rows.length > 0) {
        applySavedRowMeta(logicalIndex, beforeRowId, savedRows);
        const updatedMeta = rowMetaRef.current[logicalIndex] || meta;
        lastPayloadRef.current.set(
          logicalIndex,
          JSON.stringify(buildRowPayload(rec, logicalIndex, updatedMeta))
        );
      }
      if (deleteIds.length > 0) {
        rowMetaRef.current[logicalIndex] = emptyRowMeta();
        lastPayloadRef.current.delete(logicalIndex);
      }

      const now = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
      updateStatusLabel(`마지막 저장: ${now}`);
      if (rows.length > 0) {
        notifyContainerDispatchDateSaved(date);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '저장 실패';
      setSaveStatus('error');
      setSaveMessage(msg);
      updateStatusLabel(msg, true);
    }
  }, [loadRows, readGridData]);

  const runPersistRow = useCallback(async (logicalIndex: number) => {
    const prev = persistInFlightRef.current.get(logicalIndex);
    const current = (async () => {
      if (prev) await prev;
      await persistRow(logicalIndex);
    })();
    persistInFlightRef.current.set(logicalIndex, current);
    try {
      await current;
    } finally {
      if (persistInFlightRef.current.get(logicalIndex) === current) {
        persistInFlightRef.current.delete(logicalIndex);
      }
    }
  }, [persistRow]);

  persistRowRef.current = runPersistRow;

  const scheduleRowSave = useCallback((logicalIndex: number) => {
    if (loadingRef.current) return;
    if (!debouncersRef.current.has(logicalIndex)) {
      debouncersRef.current.set(
        logicalIndex,
        debounce((idx: number) => { void persistRowRef.current(idx); }, 300)
      );
    }
    debouncersRef.current.get(logicalIndex)!(logicalIndex);
  }, []);

  const handleAfterChange = useCallback((changes: any, source: string) => {
    if (!changes || loadingRef.current) return;
    if (source === 'loadData') return;
    if (source === 'cd-strict-reject') return;

    if (
      source === 'edit'
      || source === 'CopyPaste.paste'
      || source === 'Autofill.fill'
      || source === 'cd-phone-format'
      || source === 'cd-amount-format'
    ) {
      amountEditorCleanupRef.current?.();
      amountEditorCleanupRef.current = null;
    }

    const hot = hotRef.current?.hotInstance;
    if (
      hot
      && (source === 'edit' || source === 'CopyPaste.paste' || source === 'Autofill.fill')
    ) {
      for (const [row, col, oldValue, newVal] of changes) {
        if (
          (col === 2 || col === 11)
          && typeof row === 'number'
          && row % ROWS_PER_RECORD === 1
          && typeof newVal === 'string'
        ) {
          const formatted = formatPhoneKoreanInput(newVal);
          if (formatted !== newVal) {
            hot.setDataAtCell(row, col, formatted, 'cd-phone-format');
          }
        }
        if (
          (col === 13 || col === 14)
          && typeof row === 'number'
          && newVal != null
          && newVal !== ''
        ) {
          const formatted = formatAmountDisplay(newVal);
          const current = String(newVal);
          if (formatted !== current) {
            hot.setDataAtCell(row, col, formatted, 'cd-amount-format');
          }
        }
        if (
          (source === 'CopyPaste.paste' || source === 'Autofill.fill')
          && typeof row === 'number'
          && typeof col === 'number'
        ) {
          const suggestSource = getSuggestSource(row, col);
          const pasted = String(newVal ?? '').trim();
          if (suggestSource && pasted) {
            const canonical = findCanonicalSuggestValue(pasted, suggestSource.options);
            if (!canonical && suggestSource.strict !== false) {
              hot.setDataAtCell(row, col, typeof oldValue === 'string' ? oldValue : '', 'cd-strict-reject');
              notifyStrictSuggestReject(row, col);
            } else if (canonical && canonical !== String(newVal ?? '')) {
              hot.setDataAtCell(row, col, canonical, 'cd-strict-normalize');
            }
          }
        }
      }
    }

    if (
      source !== 'edit'
      && source !== 'CopyPaste.paste'
      && source !== 'Autofill.fill'
      && source !== 'cd-phone-format'
      && source !== 'cd-amount-format'
      && source !== 'cd-strict-normalize'
    ) return;

    // 자동완성 셀: 저장 전에 목록 검증/정규화
    if (source === 'edit' && hot) {
      for (const [row, col, oldValue, newVal] of changes) {
        if (typeof row !== 'number' || typeof col !== 'number') continue;
        const suggestSource = getSuggestSource(row, col);
        if (!suggestSource) continue;

        suggestSelectLockRef.current = true;
        closeCellSuggest();

        const raw = String(newVal ?? '');
        const val = raw.trim();
        if (!val) {
          if (raw !== '') {
            hot.setDataAtCell(row, col, '', 'cd-strict-reject');
          }
          suggestOriginalRef.current = null;
          continue;
        }
        const canonical = findCanonicalSuggestValue(val, suggestSource.options);
        if (!canonical) {
          if (suggestSource.strict === false) {
            if (val !== raw) {
              hot.setDataAtCell(row, col, val, 'cd-strict-normalize');
            }
            suggestOriginalRef.current = null;
            continue;
          }
          const revert = typeof oldValue === 'string' ? oldValue : (
            suggestOriginalRef.current
            && suggestOriginalRef.current.row === row
            && suggestOriginalRef.current.col === col
              ? suggestOriginalRef.current.value
              : ''
          );
          hot.setDataAtCell(row, col, revert, 'cd-strict-reject');
          suggestOriginalRef.current = null;
          notifyStrictSuggestReject(row, col);
          continue;
        }
        if (canonical !== raw) {
          hot.setDataAtCell(row, col, canonical, 'cd-strict-normalize');
        }
        suggestOriginalRef.current = null;
      }
      window.setTimeout(() => {
        suggestSelectLockRef.current = false;
      }, 0);
    }

    const logicalIndexes = new Set<number>();
    for (const [row, col, , newVal] of changes) {
      if (typeof row !== 'number') continue;
      // 방금 목록 외로 거부된 변경은 저장하지 않음
      if (typeof col === 'number' && getSuggestSource(row, col)) {
        const suggestSource = getSuggestSource(row, col)!;
        const val = String(newVal ?? '').trim();
        if (
          val
          && suggestSource.strict !== false
          && !isStrictSuggestValue(val, suggestSource.options)
        ) continue;
      }
      logicalIndexes.add(logicalIndexFromVisualRow(row));
    }
    logicalIndexes.forEach(idx => scheduleRowSave(idx));
  }, [scheduleRowSave, getSuggestSource, closeCellSuggest, notifyStrictSuggestReject]);

  const handleAfterBeginEditing = useCallback((row: number, col: number) => {
    suggestEditorCleanupRef.current?.();
    suggestEditorCleanupRef.current = null;
    updateCellSuggest(null);

    if (col === 13 || col === 14) {
      amountEditorCleanupRef.current?.();
      amountEditorCleanupRef.current = null;

      const hot = hotRef.current?.hotInstance;
      const editor = hot?.getActiveEditor() as { TEXTAREA?: HTMLTextAreaElement } | undefined;
      const textarea = editor?.TEXTAREA;
      if (!textarea) return;

      const initial = formatAmountKoreanInput(textarea.value);
      if (textarea.value !== initial) {
        textarea.value = initial;
        textarea.setSelectionRange(initial.length, initial.length);
      }

      const onInput = () => {
        const formatted = formatAmountKoreanInput(textarea.value);
        if (textarea.value !== formatted) {
          textarea.value = formatted;
          textarea.setSelectionRange(formatted.length, formatted.length);
        }
      };
      textarea.addEventListener('input', onInput);
      amountEditorCleanupRef.current = () => textarea.removeEventListener('input', onInput);
      return;
    }

    const source = getSuggestSource(row, col);
    if (!source) return;

    const hot = hotRef.current?.hotInstance;
    const td = hot?.getCell(row, col);
    const editor = hot?.getActiveEditor() as { TEXTAREA?: HTMLTextAreaElement } | undefined;
    const textarea = editor?.TEXTAREA;
    if (!td || !textarea) return;

    suggestSelectLockRef.current = false;
    suggestOriginalRef.current = {
      row,
      col,
      value: String(hot?.getDataAtCell(row, col) ?? ''),
    };
    suggestEditingCellRef.current = { row, col };

    const refreshSuggest = () => {
      if (suggestSelectLockRef.current) return;
      const query = textarea.value;
      const { items, total } = filterSuggestOptions(query, source.options, 12);
      if (items.length === 0) {
        updateCellSuggest(null);
        return;
      }
      const rect = td.getBoundingClientRect();
      const position = computeSuggestDropdownPosition(rect, items.length);
      const prev = cellSuggestRef.current;
      updateCellSuggest({
        title: source.title,
        items,
        totalCount: total,
        row,
        col,
        position,
        activeIndex: prev && prev.row === row && prev.col === col
          ? Math.min(prev.activeIndex, items.length - 1)
          : 0,
        query,
      });
    };

    refreshSuggest();
    textarea.addEventListener('input', refreshSuggest);
    suggestEditorCleanupRef.current = () => textarea.removeEventListener('input', refreshSuggest);
  }, [getSuggestSource, updateCellSuggest]);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!cellSuggestRef.current && !suggestEditingCellRef.current) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('.cd-grid-suggest')) return;
      if (target?.closest('.handsontableInput')) return;
      const pending = suggestEditingCellRef.current;
      closeCellSuggest();
      if (pending) {
        scheduleSuggestValidation(pending.row, pending.col);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [closeCellSuggest, scheduleSuggestValidation]);

  const handleSelectSuggest = useCallback((value: string) => {
    const suggest = cellSuggestRef.current;
    if (!suggest) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    const { row, col } = suggest;
    suggestSelectLockRef.current = true;
    closeCellSuggest();

    const editor = hot.getActiveEditor() as {
      isOpened?: () => boolean;
      TEXTAREA?: HTMLTextAreaElement;
      setValue?: (next: string) => void;
      finishEditing?: (restoreOriginalValue?: boolean, ctrlDown?: boolean) => void;
    } | undefined;

    if (editor?.isOpened?.()) {
      if (typeof editor.setValue === 'function') {
        editor.setValue(value);
      } else if (editor.TEXTAREA) {
        editor.TEXTAREA.value = value;
      }
      editor.finishEditing?.(false, false);
    } else {
      hot.setDataAtCell(row, col, value, 'edit');
    }

    // finishEditing / setValue 과정에서 목록이 다시 뜰 수 있어 한 번 더 닫음
    window.setTimeout(() => {
      suggestOverlayRef.current?.hide();
      cellSuggestRef.current = null;
      suggestSelectLockRef.current = false;
    }, 0);
  }, [closeCellSuggest]);

  const handleSuggestActiveIndexChange = useCallback((index: number) => {
    const suggest = cellSuggestRef.current;
    if (!suggest) return;
    cellSuggestRef.current = { ...suggest, activeIndex: index };
  }, []);

  const handleBeforeKeyDown = useCallback((event: KeyboardEvent) => {
    const suggest = cellSuggestRef.current;
    if (suggest) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = Math.min(suggest.activeIndex + 1, suggest.items.length - 1);
        cellSuggestRef.current = { ...suggest, activeIndex: nextIndex };
        suggestOverlayRef.current?.setActiveIndex(nextIndex);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const nextIndex = Math.max(suggest.activeIndex - 1, 0);
        cellSuggestRef.current = { ...suggest, activeIndex: nextIndex };
        suggestOverlayRef.current?.setActiveIndex(nextIndex);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const selected = suggest.items[suggest.activeIndex];
        if (selected) {
          handleSelectSuggest(selected);
        } else {
          const hot = hotRef.current?.hotInstance;
          const editor = hot?.getActiveEditor() as { TEXTAREA?: HTMLTextAreaElement } | undefined;
          const typed = String(editor?.TEXTAREA?.value ?? '').trim();
          if (typed && !findCanonicalSuggestValue(typed, getSuggestSource(suggest.row, suggest.col)?.options ?? [])) {
            notifyStrictSuggestReject(suggest.row, suggest.col);
          }
          suggestSelectLockRef.current = true;
          closeCellSuggest();
          hot?.getActiveEditor()?.finishEditing?.(true, false);
          suggestSelectLockRef.current = false;
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        suggestSelectLockRef.current = true;
        closeCellSuggest();
        const hot = hotRef.current?.hotInstance;
        hot?.getActiveEditor()?.finishEditing?.(true, false);
        suggestSelectLockRef.current = false;
      }
      return;
    }

    if (event.key !== 'Enter') return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const editor = hot.getActiveEditor() as {
      isOpened?: () => boolean;
      TEXTAREA?: HTMLTextAreaElement;
    } | undefined;
    if (!editor?.isOpened?.()) return;

    const selected = hot.getSelectedLast();
    if (!selected) return;
    const [row, col] = selected;
    const source = getSuggestSource(row, col);
    if (!source || source.strict === false) return;

    const typed = String(editor.TEXTAREA?.value ?? '').trim();
    if (!typed || findCanonicalSuggestValue(typed, source.options)) return;

    notifyStrictSuggestReject(row, col);
  }, [closeCellSuggest, getSuggestSource, handleSelectSuggest, notifyStrictSuggestReject]);

  const handleAddRecord = () => {
    const records = gridToLogicalRecords(readGridData());
    records.push(emptyLogicalRecord());
    const next = logicalRecordsToGrid(records);
    applyGridData(next);
    rowMetaRef.current.push(emptyRowMeta());
  };

  /** 삽입·삭제 후 밀린 건들의 sort_order 를 DB에 반영 */
  const persistSortOrdersFrom = useCallback(async (fromIndex: number) => {
    if (loadingRef.current) return;
    const date = dispatchDateRef.current;
    if (!date) return;

    const records = gridToLogicalRecords(readGridData());
    const rows: Array<Record<string, unknown>> = [];
    for (let i = fromIndex; i < records.length; i++) {
      const meta = rowMetaRef.current[i];
      if (!meta?.rowId || isLogicalRecordEmpty(records[i])) continue;
      rows.push(buildRowPayload(records[i], i, meta));
    }
    if (rows.length === 0) return;

    try {
      const res = await fetch(`/api/container-dispatch/${date}/rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows,
          deleteIds: [],
          ...currentUserPayload(),
        }),
      });
      const body = await res.json();
      if (res.status === 409) {
        setSaveStatus('error');
        setSaveMessage('다른 사용자가 수정했습니다. 새로고침합니다.');
        updateStatusLabel('다른 사용자가 수정했습니다. 새로고침합니다.', true);
        await loadRows(date);
        return;
      }
      if (!res.ok) throw new Error(body.error || '순서 저장 실패');

      const savedRows: ContainerRowApi[] = body.rows || [];
      rows.forEach((payload) => {
        const logicalIndex = Number(payload.sortOrder);
        const beforeRowId = typeof payload.rowId === 'string' ? payload.rowId : null;
        applySavedRowMeta(logicalIndex, beforeRowId, savedRows);
        const updatedMeta = rowMetaRef.current[logicalIndex];
        const rec = records[logicalIndex];
        if (updatedMeta && rec) {
          lastPayloadRef.current.set(
            logicalIndex,
            JSON.stringify(buildRowPayload(rec, logicalIndex, updatedMeta)),
          );
        }
      });
      const now = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
      updateStatusLabel(`마지막 저장: ${now}`);
      notifyContainerDispatchDateSaved(date);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '순서 저장 실패';
      setSaveStatus('error');
      setSaveMessage(msg);
      updateStatusLabel(msg, true);
    }
  }, [loadRows, readGridData]);

  const handleInsertRecordRef = useRef<(logicalIndex: number, where: 'above' | 'below') => void>(() => {});

  const handleInsertRecord = useCallback((logicalIndex: number, where: 'above' | 'below') => {
    const insertAt = where === 'above' ? logicalIndex : logicalIndex + 1;
    const records = gridToLogicalRecords(readGridData());
    if (insertAt < 0 || insertAt > records.length) return;

    const hot = hotRef.current?.hotInstance as Handsontable | undefined;
    const holder = hot?.rootElement?.querySelector('.ht_master .wtHolder') as HTMLElement | null;
    const scrollTop = holder?.scrollTop ?? 0;
    const scrollLeft = holder?.scrollLeft ?? 0;

    records.splice(insertAt, 0, emptyLogicalRecord());
    applyGridData(logicalRecordsToGrid(records));
    rowMetaRef.current.splice(insertAt, 0, emptyRowMeta());

    const shifted = new Map<number, string>();
    lastPayloadRef.current.forEach((value, key) => {
      shifted.set(key >= insertAt ? key + 1 : key, value);
    });
    lastPayloadRef.current = shifted;

    void persistSortOrdersFrom(insertAt + 1);

    // 스크롤 유지 (행 삭제와 동일하게 화면이 튀지 않게)
    requestAnimationFrame(() => {
      const nextHot = hotRef.current?.hotInstance as Handsontable | undefined;
      const nextHolder = nextHot?.rootElement?.querySelector('.ht_master .wtHolder') as HTMLElement | null;
      if (nextHolder) {
        nextHolder.scrollTop = scrollTop;
        nextHolder.scrollLeft = scrollLeft;
      }
      // selectCell 5번째 인자 false = 스크롤하지 않음
      nextHot?.selectCell(insertAt * ROWS_PER_RECORD, 0, insertAt * ROWS_PER_RECORD, 0, false);
    });
  }, [readGridData, applyGridData, persistSortOrdersFrom]);

  handleInsertRecordRef.current = handleInsertRecord;

  const handleDeleteRecordRef = useRef<(logicalIndex: number) => void>(() => {});

  const handleDeleteRecord = useCallback((logicalIndex: number) => {
    const meta = rowMetaRef.current[logicalIndex];
    if (meta?.rowId) {
      void persistDeletes([meta.rowId]);
    }
    const records = gridToLogicalRecords(readGridData());
    records.splice(logicalIndex, 1);
    const minCount = minLogicalRecordsRef.current;
    if (records.length < minCount) {
      while (records.length < minCount) {
        records.push(emptyLogicalRecord());
      }
    }
    const next = logicalRecordsToGrid(records);
    applyGridData(next);
    rowMetaRef.current.splice(logicalIndex, 1);
    while (rowMetaRef.current.length < minLogicalRecordsRef.current) {
      rowMetaRef.current.push(emptyRowMeta());
    }

    const shifted = new Map<number, string>();
    lastPayloadRef.current.forEach((value, key) => {
      if (key === logicalIndex) return;
      shifted.set(key > logicalIndex ? key - 1 : key, value);
    });
    lastPayloadRef.current = shifted;

    void persistSortOrdersFrom(logicalIndex);
  }, [persistDeletes, readGridData, applyGridData, persistSortOrdersFrom]);

  handleDeleteRecordRef.current = handleDeleteRecord;

  const cells = useCallback((row: number, col: number) => {
    const props: Record<string, unknown> = {
      className: 'htCenter htMiddle',
    };
    const isTopRow = row % ROWS_PER_RECORD === 0;
    if (col === 0 || col === 3 || col === 10 || col === 14 || col === 15) {
      props.className += ' cd-span2-cell';
    }
    if (col === 1 && isTopRow) {
      props.className += ' cd-shipper-top';
      props.renderer = shipperTopRenderer;
    }
    if ((col === 1 || col === 2) && !isTopRow) {
      props.className += ' cd-shipper-bottom';
    }
    if ((col === 2 || col === 11) && !isTopRow) {
      props.style = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
    }
    // 청구액(상단) / 하불액(하단) — 정산일보 청구운송비·지불운송비와 동일 색
    if (col === 13) {
      props.className += isTopRow ? ' billing-amount' : ' payment-amount';
    }
    return props;
  }, [shipperTopRenderer]);

  const columns = useMemo(
    () => Array.from({ length: GRID_COL_COUNT }, (_, idx) => {
      if (idx === 13 || idx === 14) {
        return {
          type: 'text' as const,
          renderer: AMOUNT_RENDERER,
        };
      }
      return {
        type: 'text' as const,
        renderer: PLACEHOLDER_RENDERER,
      };
    }),
    []
  );

  const afterGetRowHeader = useCallback((row: number, TH: HTMLTableCellElement) => {
    if (row % ROWS_PER_RECORD !== 0) {
      TH.innerHTML = '';
      return;
    }
    TH.innerHTML = String(Math.floor(row / ROWS_PER_RECORD) + 1);
  }, []);

  const handleAfterColumnResize = useCallback((newSize: number, column: number) => {
    if (column < 0 || column >= GRID_COL_COUNT) return;
    setColWidths(prev => {
      const next = [...prev];
      next[column] = newSize;
      return next;
    });
  }, []);

  const sidebarReserve = isFullScreen
    ? 0
    : (sidebarCollapsed ? SIDEBAR_RESERVE_COLLAPSED : SIDEBAR_RESERVE_EXPANDED);

  if (!selectedDate) {
    return (
      <div style={{
        width: '100%',
        height: isFullScreen ? '100vh' : 'calc(100vh - 48px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 28,
        color: '#1976d2',
        fontWeight: 700,
      }}>
        날짜를 선택하세요
      </div>
    );
  }

  return (
    <div className="container-dispatch-page" style={{
      padding: '6px 12px',
      paddingRight: 12 + sidebarReserve,
      height: isFullScreen ? '100vh' : 'calc(100vh - 48px)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      <style>{`
        .cd-span2-cell { background: #fafafa !important; }
        .cd-shipper-top { background: #fffde7 !important; }
        .cd-shipper-bottom { background: #fff8e1 !important; }
        /* 정산일보 청구운송비·지불운송비와 동일 글자색 (배경은 기본 유지) */
        .container-dispatch-page .billing-amount {
          color: #d32f2f !important;
          font-weight: 700 !important;
        }
        .container-dispatch-page .payment-amount {
          color: #1976d2 !important;
          font-weight: 700 !important;
        }
        .handsontable td.area { background: #e3f2fd !important; }
        .handsontable .cd-cell-hint {
          color: #b0b8c4;
          font-style: italic;
          font-size: 12px;
          font-weight: 400;
        }
        .handsontable td.current .cd-cell-hint { display: none; }
        .container-dispatch-page .ht_master_wrapper td.has-route-memo {
          padding-right: 14px !important;
        }
        .container-dispatch-page .route-memo-indicator {
          position: absolute;
          top: 0;
          right: 0;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 9px 9px 0;
          border-color: transparent #e65100 transparent transparent;
          pointer-events: none;
        }
        .route-memo-hover-popup {
          width: min(300px, calc(100vw - 24px));
          max-height: calc(100vh - 16px);
          display: flex;
          flex-direction: column;
          background: #fffde7;
          border: 1px solid #f9a825;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          padding: 10px 12px;
          z-index: 90;
          pointer-events: auto;
          box-sizing: border-box;
        }
        .route-memo-hover-popup__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          font-size: 12px;
          font-weight: 700;
          color: #6d4c41;
          margin-bottom: 6px;
        }
        .route-memo-hover-popup__close {
          border: none;
          background: transparent;
          color: #757575;
          font-size: 18px;
          line-height: 1;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
        }
        .route-memo-hover-popup__close:hover {
          background: rgba(0, 0, 0, 0.06);
          color: #424242;
        }
        .route-memo-hover-popup__body {
          font-size: 13px;
          line-height: 1.5;
          color: #212121;
          white-space: pre-wrap;
          word-break: break-word;
          max-height: min(240px, calc(100vh - 120px));
          overflow-y: auto;
          flex: 1 1 auto;
          min-height: 0;
        }
        .route-memo-hover-popup__actions {
          display: flex;
          justify-content: flex-end;
          gap: 6px;
          margin-top: 8px;
          flex-shrink: 0;
        }
        .route-memo-hover-popup__btn {
          border: none;
          background: #1976d2;
          color: #fff;
          border-radius: 4px;
          padding: 4px 10px;
          font-size: 12px;
          cursor: pointer;
        }
        .route-memo-hover-popup__btn--secondary {
          background: #fff;
          color: #1976d2;
          border: 1px solid #1976d2;
        }
        .htContextMenu table.htCore tbody tr.htSeparator + tr.htDisabled td.htItemWrapper {
          height: 10px;
          padding: 6px 0 !important;
          pointer-events: none;
          border-top: none !important;
          border-bottom: none !important;
        }
        .htContextMenu table.htCore tbody tr.htSeparator + tr.htDisabled td.htItemWrapper .htItem {
          display: none;
        }
        .htContextMenu table.htCore tbody tr.htSeparator + tr.htDisabled + tr td.htItemWrapper {
          padding-top: 4px;
        }
      `}</style>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        marginBottom: 4,
      }}
      >
        <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          일자
          <input
            type="date"
            value={dispatchDate}
            onChange={e => handleDateChange(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 }}
          />
        </label>
        <button
          type="button"
          onClick={handleAddRecord}
          style={{ padding: '5px 12px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
        >
          + 건 추가
        </button>
        {saveStatus === 'error' && saveMessage && (
          <span style={{ fontSize: 12, color: '#c62828', fontWeight: 600 }}>
            {saveMessage}
          </span>
        )}
        {lastEditInfo && pendingChangesCount === 0 && (
          <span style={{
            fontSize: 12,
            color: '#1e40af',
            padding: '2px 8px',
            borderRadius: 4,
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
          }}
          >
            최근 편집 {lastEditInfo.name}{' '}
            {(() => {
              const d = new Date(lastEditInfo.timestamp);
              return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
            })()}
          </span>
        )}
        {pendingChangesCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setPendingChangesCount(0);
              if (dispatchDate) void loadRows(dispatchDate);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 10px',
              borderRadius: 5,
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              fontSize: 12,
              color: '#92400e',
              fontWeight: 600,
              cursor: 'pointer',
              animation: 'pulse 2s infinite',
            }}
            title="다른 사용자의 변경사항을 새로고침합니다"
          >
            {pendingChangesCount}건 변경됨 · 새로고침
          </button>
        )}
        <span ref={statusLabelRef} style={{ fontSize: 12, color: '#888' }} />

        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
        </div>
      </div>

      <div ref={gridWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <>
          <ContainerDispatchGridHeader
            colWidths={colWidths}
            rowHeaderWidth={CD_ROW_HEADER_WIDTH}
            tableWidth={tableWidth}
          />
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', width: tableWidth }}>
            <HotTable
              ref={hotRef}
              data={tableData}
              colHeaders={false}
              mergeCells={mergeCells}
              cells={cells}
              rowHeaders
              rowHeaderWidth={CD_ROW_HEADER_WIDTH}
              afterGetRowHeader={afterGetRowHeader}
              width={tableWidth}
              height={hotHeight}
              licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
              stretchH="none"
              colWidths={colWidths}
              manualColumnResize
              rowHeights={ROW_HEIGHT}
              contextMenu={{
                items: {
                  route_memo_edit: {
                    name: function (this: Handsontable) {
                      const sel = this.getSelectedLast();
                      if (!sel) return '메모';
                      const logicalIdx = logicalIndexFromVisualRow(sel[0]);
                      const memo = getShipperMemoRef.current(logicalIdx);
                      return memo ? '메모 편집' : '메모 추가';
                    },
                    disabled: function (this: Handsontable) {
                      const sel = this.getSelectedLast();
                      if (!sel) return true;
                      const logicalIdx = logicalIndexFromVisualRow(sel[0]);
                      return !isShipperMemoEligibleRef.current(logicalIdx);
                    },
                    callback: function (_key: string, selection: any) {
                      const logicalIdx = logicalIndexFromVisualRow(selection[0].start.row);
                      openShipperMemoEditorRef.current(logicalIdx);
                    },
                  },
                  route_memo_delete: {
                    name: '메모 삭제',
                    disabled: function (this: Handsontable) {
                      const sel = this.getSelectedLast();
                      if (!sel) return true;
                      const logicalIdx = logicalIndexFromVisualRow(sel[0]);
                      const meta = rowMetaRef.current[logicalIdx];
                      const memo = getShipperMemoRef.current(logicalIdx);
                      return !meta?.rowId || memo === '';
                    },
                    callback: function (_key: string, selection: any) {
                      const logicalIdx = logicalIndexFromVisualRow(selection[0].start.row);
                      if (!window.confirm('메모를 삭제할까요?')) return;
                      void saveShipperMemoRef.current(logicalIdx, '');
                    },
                  },
                  '---------': {} as Record<string, never>,
                  cd_insert_above: {
                    name: '위에 건 추가',
                    callback(this: Handsontable) {
                      const sel = this.getSelectedLast();
                      if (!sel) return;
                      handleInsertRecordRef.current(logicalIndexFromVisualRow(sel[0]), 'above');
                    },
                  },
                  cd_insert_below: {
                    name: '아래에 건 추가',
                    callback(this: Handsontable) {
                      const sel = this.getSelectedLast();
                      if (!sel) return;
                      handleInsertRecordRef.current(logicalIndexFromVisualRow(sel[0]), 'below');
                    },
                  },
                  cd_row_delete_gap: {
                    name: ' ',
                    disabled() { return true; },
                  },
                  cd_delete_record: {
                    name: '행 삭제',
                    callback(this: Handsontable) {
                      const sel = this.getSelectedLast();
                      if (!sel) return;
                      const logicalIdx = logicalIndexFromVisualRow(sel[0]);
                      if (window.confirm(`${logicalIdx + 1}번 건을 삭제하시겠습니까?`)) {
                        handleDeleteRecordRef.current(logicalIdx);
                      }
                    },
                  },
                },
              }}
              afterChange={handleAfterChange}
              afterBeginEditing={handleAfterBeginEditing}
              beforeKeyDown={handleBeforeKeyDown}
              afterColumnResize={handleAfterColumnResize}
              afterContextMenuHide={resumeShipperMemoHover}
              columns={columns}
            />
          </div>
        </>
      </div>

      <GridSuggestOverlay
        ref={suggestOverlayRef}
        onSelect={handleSelectSuggest}
        onClose={closeCellSuggest}
        onActiveIndexChange={handleSuggestActiveIndexChange}
      />

      {shipperMemoHover && (
        <div
          className="route-memo-hover-popup"
          style={{
            position: 'fixed',
            left: shipperMemoHover.left,
            top: shipperMemoHover.top,
            zIndex: 90,
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            suppressShipperMemoHover();
          }}
        >
          <div className="route-memo-hover-popup__header">
            <span>메모</span>
            <button
              type="button"
              className="route-memo-hover-popup__close"
              aria-label="메모 닫기"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => dismissShipperMemoHover()}
            >
              ×
            </button>
          </div>
          <div className="route-memo-hover-popup__body">{shipperMemoHover.text}</div>
          <div className="route-memo-hover-popup__actions">
            <button
              type="button"
              className="route-memo-hover-popup__btn route-memo-hover-popup__btn--secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => copyShipperMemoText(shipperMemoHover.text)}
            >
              복사
            </button>
            <button
              type="button"
              className="route-memo-hover-popup__btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => openShipperMemoEditor(logicalIndexFromVisualRow(shipperMemoHover.visualRow))}
            >
              편집
            </button>
          </div>
        </div>
      )}
      <Dialog
        open={!!shipperMemoEdit}
        onClose={() => !shipperMemoSaving && setShipperMemoEdit(null)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: focusShipperMemoInput,
        }}
      >
        <DialogTitle>메모</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={shipperMemoInputRef}
            autoFocus
            multiline
            minRows={5}
            maxRows={12}
            fullWidth
            placeholder="셀에 붙는 메모입니다. 표에는 짧게만 보이고, 마우스를 올리면 전체 내용이 표시됩니다."
            value={shipperMemoEdit?.text ?? ''}
            onChange={(e) => {
              const text = e.target.value;
              setShipperMemoEdit((prev) => (prev ? { ...prev, text } : prev));
            }}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            color="error"
            disabled={
              shipperMemoSaving ||
              !shipperMemoEdit ||
              (
                !String(shipperMemoEdit.text ?? '').trim() &&
                !String(rowMetaRef.current[shipperMemoEdit.logicalIndex]?.shipperMemo ?? '').trim()
              )
            }
            onClick={async () => {
              if (!shipperMemoEdit) return;
              if (!window.confirm('메모를 삭제할까요?')) return;
              setShipperMemoSaving(true);
              const ok = await saveShipperMemo(shipperMemoEdit.logicalIndex, '');
              setShipperMemoSaving(false);
              if (ok) setShipperMemoEdit(null);
            }}
          >
            삭제
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outlined"
              disabled={shipperMemoSaving || !String(shipperMemoEdit?.text ?? '').trim()}
              onClick={() => {
                if (!shipperMemoEdit) return;
                copyShipperMemoText(shipperMemoEdit.text);
              }}
            >
              클립보드 복사
            </Button>
            <Button onClick={() => setShipperMemoEdit(null)} disabled={shipperMemoSaving}>
              취소
            </Button>
            <Button
              variant="contained"
              disabled={shipperMemoSaving}
              onClick={async () => {
                if (!shipperMemoEdit) return;
                setShipperMemoSaving(true);
                const ok = await saveShipperMemo(shipperMemoEdit.logicalIndex, shipperMemoEdit.text.trim());
                setShipperMemoSaving(false);
                if (ok) setShipperMemoEdit(null);
              }}
            >
              {shipperMemoSaving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={shipperMemoCopySnackbar}
        autoHideDuration={2200}
        onClose={() => setShipperMemoCopySnackbar(false)}
        message="메모를 복사했습니다"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={Boolean(suggestRejectSnackbar)}
        autoHideDuration={3500}
        onClose={() => setSuggestRejectSnackbar(null)}
        message={suggestRejectSnackbar ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            padding: 40,
            borderRadius: 16,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: 22,
            fontWeight: 700,
            color: '#1976d2',
          }}>
            <div className="spinner" style={{ marginBottom: 18 }}>
              <svg width="48" height="48" viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke="#1976d2" strokeWidth="6" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)">
                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
            로딩 중...
          </div>
        </div>
      )}
    </div>
  );
};

export default ContainerDispatchReportPage;
